import { MotiveResourcesPage } from "@local-cms/components/MotiveResourcesPage";
import { motiveArticles } from "@local-cms/data/motiveArticles";

import type { TenantConfig } from "../types";

import appleTouchIcon from "./icons/apple-touch-icon.png";
import favicon from "./icons/favicon.ico";
import icon192 from "./icons/icon-192x192.png";
import icon512 from "./icons/icon-512x512.png";
import { branding } from "./branding";
import build from "./build.json";
import { motiveFeatures } from "./features";
import motiveBackgroundImage from "./motive-auth-background.png";
import motiveIcon from "./motive-icon.svg?react";
import motiveLogoDark from "./motive-logo-black-2.svg";
import motiveLogoLight from "./motive-logo-light.svg";
import motiveSmallLogoLight from "./motive-logo-small-light.svg";
import motiveSmallLogoDark from "./motive-small-logo-black.svg";

export const tenantConfig: TenantConfig = {
  metadata: {
    name: "Motive",
    companyId: "mmm90110-mmmm-mmmm-mmmm-m90110m90110",
    legalNames: {
      globalManagement: "Motive Global Management",
      managementInternational: "Motive Management International LLP",
    },
    locale: "en-US",
    defaultCurrency: "USD",
  },

  branding: {
    ...branding,

    logos: {
      logoDark: motiveLogoLight,
      logoLight: motiveLogoDark,
      smallLogoDark: motiveSmallLogoLight,
      smallLogoLight: motiveSmallLogoDark,
      sidebar: {
        smallSidebarLogo: motiveSmallLogoLight,
        sidebarLogo: motiveLogoLight,
      },
      auth: {
        logo: motiveLogoLight,
        smallLogo: motiveSmallLogoLight,
      },
    },
    assets: {
      auth: {
        backgroundImage: motiveBackgroundImage,
      },
      assetClassLogos: {
        PRIVATE_CREDIT: motiveIcon,
        PRIVATE_EQUITY: motiveIcon,
        HYBRID: motiveIcon,
        REAL_ASSETS: motiveIcon,
      },
    },

    icons: {
      favicon,
      appleTouch: appleTouchIcon,
      icon192,
      icon512,
    },

    defaultMode: "light",
    borderRadius: "8px",
  },

  features: motiveFeatures,

  contacts: {
    employeeEmail: "subscriptions@motivepartners.com",
    investorEmail: "subscriptions@motivepartners.com",
  },

  links: {
    privacyPolicy: "https://www.motive.com/privacy-policy",
    footer: [
      {
        title: "Privacy Statement",
        path: "https://www.motivepartners.com/privacy-statement",
      },
      {
        title: "Legal Disclaimer",
        path: "https://cdn.prod.website-files.com/68a5cb86a568b8c8b703455d/68fe37da1362ae2767338b68_Motive%20Capital%20Management%20LLC%20-%20Website%20Legal%20Disclaimer%202025.pdf",
      },
    ],
    registration: [
      {
        title: "Forward-Looking Statements",
        path: "https://www.motive.com/forward-looking-statements",
      },
      {
        title: "Legal Disclaimer",
        path: "https://cdn.prod.website-files.com/68a5cb86a568b8c8b703455d/68fe37da1362ae2767338b68_Motive%20Capital%20Management%20LLC%20-%20Website%20Legal%20Disclaimer%202025.pdf",
      },
    ],
  },

  content: {
    tabTitles: build.portals,
    footerText:
      "© Motive Capital Management, LLC. {{year}} All Rights Reserved.",
    loginFaq: [
      {
        title: "What is the Motive Investor Portal?",
        body: "The Motive Investor Portal is a secure, centralized platform designed for Motive's investors to access available funds for them to invest in on the portal. Future expansions of the portal will also allow the user to review their investments' reporting data, manage their account information and access portfolio analytics functionalities.",
      },
      {
        title: "How do I gain access to the Motive Investor Portal?",
        body: "The Investor Portal will be accessible through any web browser via desktop or mobile device. After being invited by the Motive team or one of your team members, you will receive an invitation email containing a secure link to register or log in to the portal. If you or a colleague did not receive an invitation, please reach out to subscriptions@motivepartners.com.",
      },
      {
        title: "What credentials do I use to log in?",
        body: 'During your first login after receiving the platform\'s welcome email, you will be asked to set up your account credentials. If you have forgotten your password, click "Forgot Password" on the sign-in page to reset your credentials. Should you have any issues logging in, please reach out to subscriptions@motivepartners.com.',
      },
      {
        title: "Is my data secure?",
        body: "Yes. The portal leverages industry-standard encryption and advanced monitoring to protect investor information. Motive adheres to strict cybersecurity and data privacy protocols across all systems, e.g. demonstrated by the platform's SOC 2 Type 2 certification.",
      },
    ],
    welcomeInvestments: [
      {
        title: "Browse through open investment opportunities",
        description: "Explore available Motive funds for future investments",
        logo: "/images/motive-welcome-0.png",
      },
      {
        title: "Subscribe digitally to funds directly on the platform",
        description: "Request, complete and manage the subscription digitally",
        logo: "/images/motive-welcome-1.png",
      },
      {
        title: "Capital calls, distributions and reports in one place",
        description:
          "Facilitate your alternative asset operations with our LP platform",
        logo: "/images/motive-welcome-2.png",
      },
    ],
    registeredProducts: [],
    defaultFundId: "ede777e4-bedf-4cbd-8fec-a1c991604942",
    preserveCompositionTilesOrder: true,
    settingsHelpFaq: [
      {
        title: "How do I update my profile?",
        body: [
          {
            type: "text",
            value:
              "The Motive team is happy to make any profile changes on your behalf. Contact us at ",
          },
          { type: "email" },
          { type: "text", value: "." },
        ],
      },
      {
        title: "How do I change my role permissions for a fund?",
        body: [
          {
            type: "text",
            value:
              "Role changes must be made by an authorized user with contact management access. You can review available roles and their permissions in our ",
          },
          { type: "permissionsLink" },
          { type: "text", value: "." },
        ],
      },
      {
        title: "The user I added did not receive an invitation email.",
        body: [
          {
            type: "text",
            value:
              "Check the email address in your Associated Users table. You can resend the invitation by clicking the more icon next to the invited user. Still having issues? Contact us at ",
          },
          { type: "email" },
          { type: "text", value: "." },
        ],
      },
      {
        title: "Why can't I change the user roles for certain funds?",
        body: [
          {
            type: "text",
            value:
              "You need contact management access for a fund to modify user roles. You can review available roles and their permissions in our ",
          },
          { type: "permissionsLink" },
          { type: "text", value: "." },
        ],
      },
    ],
  },

  cms: {
    provider: "local",
    resourcesPageComponent: MotiveResourcesPage,
    articles: motiveArticles,
  },
} as const;
